import {
  ALPHABET,
  ALPHABET_SIZE,
  applyTextOptions,
  type CipherTextOptions,
  foldDiacritics,
  groupLetters,
  isLowerCaseLetter,
  letterIndex,
  lettersOnly,
} from "@/lib/tool-kit/ciphers/alphabet";

/**
 * The text handling the keyed letter-by-letter ciphers share (Beaufort, autokey, Porta).
 *
 * Each of them turns one A–Z letter into one A–Z letter using a key letter, so the
 * rules around the letters are identical and are made once here:
 *
 * - accented Latin letters are enciphered as their base letter (é → E);
 * - a letter keeps its case, and everything that is not an A–Z letter — spaces,
 *   digits, punctuation, letters of other alphabets — passes through unchanged and does
 *   not use up a key letter;
 * - every enciphered letter is recorded as a step, so a widget can show the working.
 */

export type CipherDirection = "encode" | "decode";

/** One enciphered letter: what went in, which key letter acted on it, what came out. */
export interface KeyedStep {
  /** The plain-text letter of the pair, in capitals — the input when encoding. */
  plain: string;
  /** The key letter that acted, in capitals. */
  key: string;
  /** The cipher-text letter of the pair, in capitals — the input when decoding. */
  cipher: string;
  /** The arithmetic behind the letter, for the working table. */
  calc: { left: number; op: "+" | "−"; right: number; raw: number };
}

export interface KeyedRun {
  output: string;
  steps: KeyedStep[];
}

/**
 * Run every A–Z letter of `text` through `apply`, which is given the capital letter and
 * its position among the letters (0, 1, 2…) and returns the step.
 *
 * The letter that comes out is `step.cipher` when encoding and `step.plain` when
 * decoding; its case follows the letter that went in.
 */
export function runKeyedLetters(
  text: string,
  direction: CipherDirection,
  apply: (letter: string, position: number) => KeyedStep
): KeyedRun {
  const steps: KeyedStep[] = [];
  let output = "";
  for (const char of foldDiacritics(text)) {
    const index = letterIndex(char);
    if (index < 0) {
      output += char;
      continue;
    }
    const step = apply(ALPHABET.charAt(index), steps.length);
    steps.push(step);
    const result = direction === "encode" ? step.cipher : step.plain;
    output += isLowerCaseLetter(char) ? result.toLowerCase() : result;
  }
  return { output, steps };
}

/** A step's arithmetic as it is written on the page: `5 − 3 = 2` or `14 − 20 = −6 ≡ 20`. */
export function describeCalc(calc: KeyedStep["calc"]): string {
  const raw = calc.raw < 0 ? `−${-calc.raw}` : `${calc.raw}`;
  const sum = `${calc.left} ${calc.op} ${calc.right} = ${raw}`;
  const reduced = ((calc.raw % ALPHABET_SIZE) + ALPHABET_SIZE) % ALPHABET_SIZE;
  return calc.raw === reduced ? sum : `${sum} ≡ ${reduced}`;
}

/**
 * The text as a widget shows it: the keep/strip options applied, or — when `group` is on —
 * letters only, in capitals, in blocks of five, the traditional way to send cipher text.
 */
export function formatCipherOutput(
  raw: string,
  options: CipherTextOptions,
  group: boolean
): string {
  if (group) {
    return groupLetters(lettersOnly(raw), 5);
  }
  return applyTextOptions(raw, options);
}
